import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, User, Loader2, ArrowRight, Tag, CheckCircle2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { couponApi } from '../services/api';

const PHONE_CODES = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
  { code: '+968', country: 'Oman', flag: '🇴🇲' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
  { code: '+962', country: 'Jordan', flag: '🇯🇴' },
  { code: '+212', country: 'Morocco', flag: '🇲🇦' },
  { code: '+213', country: 'Algeria', flag: '🇩🇿' },
  { code: '+216', country: 'Tunisia', flag: '🇹🇳' },
  { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
  { code: '+970', country: 'Palestine', flag: '🇵🇸' },
  { code: '+964', country: 'Iraq', flag: '🇮🇶' },
  { code: '+963', country: 'Syria', flag: '🇸🇾' },
  { code: '+249', country: 'Sudan', flag: '🇸🇩' },
  { code: '+218', country: 'Libya', flag: '🇱🇾' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
].sort((a, b) => a.country.localeCompare(b.country));

export default function BillingInfoModal({ isOpen, onClose, onSubmit, initialData, isProcessing }) {
  const { t } = useTranslation();
  const { isRTL } = useTheme();

  // Sort out the initial phone number into code and local parts
  const extractPhoneParts = (fullPhone) => {
    if (!fullPhone) return { code: '+20', local: '' };
    const matchingCode = PHONE_CODES.find(pc => fullPhone.startsWith(pc.code));
    if (matchingCode) {
      return {
        code: matchingCode.code,
        local: fullPhone.slice(matchingCode.code.length).replace(/^0+/, '')
      };
    }
    return { code: '+20', local: fullPhone.replace(/^\+/, '') };
  };

  const phoneParts = extractPhoneParts(initialData?.phone_number);

  const [formData, setFormData] = useState({
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    phoneCode: phoneParts.code,
    phoneNumber: phoneParts.local,
    couponCode: ''
  });

  const [couponValidationStatus, setCouponValidationStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [validatedCouponData, setValidatedCouponData] = useState(null);
  const [couponErrorMsg, setCouponErrorMsg] = useState('');

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      const parts = extractPhoneParts(initialData?.phone_number);
      setFormData({
        first_name: initialData?.first_name || '',
        last_name: initialData?.last_name || '',
        phoneCode: parts.code,
        phoneNumber: parts.local,
        couponCode: ''
      });
      setErrors({});
      setCouponValidationStatus('idle');
      setValidatedCouponData(null);
      setCouponErrorMsg('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleValidateCoupon = async () => {
    if (!formData.couponCode.trim()) return;
    try {
      setCouponValidationStatus('loading');
      setCouponErrorMsg('');
      const res = await couponApi.validateCoupon(formData.couponCode.trim());
      setValidatedCouponData(res);
      setCouponValidationStatus('success');
    } catch (err) {
      console.error(err);
      setCouponValidationStatus('error');
      setCouponErrorMsg(err.message || t('billing.invalidCoupon', 'Invalid or expired promo code'));
      setValidatedCouponData(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.first_name.trim()) newErrors.first_name = t('billing.firstNameRequired', 'First name is required');
    if (!formData.last_name.trim()) newErrors.last_name = t('billing.lastNameRequired', 'Last name is required');
    
    const plainPhone = formData.phoneNumber.replace(/\D/g, '');
    if (!plainPhone || plainPhone.length < 5) {
      newErrors.phoneNumber = t('billing.phoneRequired', 'Valid phone number is required');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Cleaned phone format
    const finalPhone = `${formData.phoneCode}${plainPhone}`;
    
    onSubmit({
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      phone_number: finalPhone,
      coupon_code: validatedCouponData?.is_valid ? formData.couponCode.trim() : null
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100]"
            onClick={!isProcessing ? onClose : undefined}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 sm:p-6 overflow-y-auto pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden pointer-events-auto"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              {/* Header */}
              <div className="relative px-6 pt-8 pb-4 sm:px-8 border-b border-slate-100">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isProcessing}
                  className="absolute top-6 right-6 p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-50"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <h2 className="text-2xl font-bold text-slate-900 mb-1.5 pr-8">
                  {t('billing.completeDetails', 'Billing Details')}
                </h2>
                <p className="text-slate-500 text-sm sm:text-base">
                  {t('billing.subtitle', 'Please provide your details to complete the subscription.')}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 sm:px-8 sm:py-6 space-y-6">
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* First Name */}
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                      {t('billing.firstName', 'First Name')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-xl border-2 transition-colors bg-slate-50 focus:bg-white outline-none ${
                          errors.first_name ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                        }`}
                        placeholder={t('billing.firstNamePlaceholder', 'John')}
                      />
                    </div>
                    {errors.first_name && <p className="text-red-500 text-xs font-medium px-1">{errors.first_name}</p>}
                  </div>

                  {/* Last Name */}
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                      {t('billing.lastName', 'Last Name')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-xl border-2 transition-colors bg-slate-50 focus:bg-white outline-none ${
                          errors.last_name ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                        }`}
                        placeholder={t('billing.lastNamePlaceholder', 'Doe')}
                      />
                    </div>
                    {errors.last_name && <p className="text-red-500 text-xs font-medium px-1">{errors.last_name}</p>}
                  </div>
                </div>

                {/* Secure Phone Number */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    {t('billing.phone', 'Phone Number')}
                  </label>
                  <div className="flex gap-2 sm:gap-3">
                    <div className="relative w-1/3 sm:w-1/4 min-w-[100px]">
                      <select
                        name="phoneCode"
                        value={formData.phoneCode}
                        onChange={handleChange}
                        className="w-full h-full px-2 sm:px-3 py-3 appearance-none rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-blue-500 outline-none font-medium text-slate-700 cursor-pointer"
                        style={{ paddingRight: '2rem' }}
                      >
                        {PHONE_CODES.map((item) => (
                          <option key={item.country} value={item.code}>
                            {item.flag} {item.code}
                          </option>
                        ))}
                      </select>
                      <div className={`absolute inset-y-0 ${isRTL ? 'left-3' : 'right-3'} flex items-center pointer-events-none`}>
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    
                    <div className="relative flex-1">
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-xl border-2 transition-colors bg-slate-50 focus:bg-white outline-none font-medium tracking-wide ${
                          errors.phoneNumber ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'
                        }`}
                        placeholder="000 0000 000"
                        dir="ltr"
                      />
                    </div>
                  </div>
                  {errors.phoneNumber && <p className="text-red-500 text-xs font-medium px-1 pt-1">{errors.phoneNumber}</p>}
                </div>

                {/* Promo Code Section */}
                <div className="space-y-2 pt-4 border-t border-slate-100">
                  <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Tag className="w-4 h-4 text-slate-400" />
                    {t('billing.promoCode', 'Promo Code (Optional)')}
                  </label>
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        name="couponCode"
                        value={formData.couponCode}
                        onChange={(e) => {
                          handleChange(e);
                          if (couponValidationStatus !== 'idle') {
                            setCouponValidationStatus('idle');
                            setValidatedCouponData(null);
                          }
                        }}
                        disabled={couponValidationStatus === 'loading' || couponValidationStatus === 'success'}
                        className={`w-full px-4 py-3 rounded-xl border-2 transition-colors bg-slate-50 focus:bg-white outline-none font-medium uppercase tracking-wider ${
                          couponValidationStatus === 'error' ? 'border-red-300 focus:border-red-500' : 
                          couponValidationStatus === 'success' ? 'border-green-300 bg-green-50' : 'border-slate-200 focus:border-blue-500'
                        }`}
                        placeholder={t('billing.promoCodePlaceholder', 'ENTER CODE')}
                      />
                    </div>
                    {couponValidationStatus !== 'success' ? (
                      <button
                        type="button"
                        onClick={handleValidateCoupon}
                        disabled={!formData.couponCode.trim() || couponValidationStatus === 'loading'}
                        className="px-6 py-3 rounded-xl bg-slate-800 text-white font-bold hover:bg-slate-900 transition-colors disabled:opacity-50 min-w-[100px] flex justify-center items-center"
                      >
                        {couponValidationStatus === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" /> : t('billing.apply', 'Apply')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, couponCode: '' }));
                          setCouponValidationStatus('idle');
                          setValidatedCouponData(null);
                        }}
                        className="px-6 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-colors min-w-[100px]"
                      >
                        {t('billing.remove', 'Remove')}
                      </button>
                    )}
                  </div>

                  {/* Validation Messages */}
                  <AnimatePresence mode="wait">
                    {couponValidationStatus === 'error' && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="flex items-center gap-2 text-red-500 text-sm font-medium mt-2">
                        <AlertCircle className="w-4 h-4" />
                        <span>{couponErrorMsg}</span>
                      </motion.div>
                    )}
                    {couponValidationStatus === 'success' && validatedCouponData && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-green-50 rounded-xl p-4 mt-2 border border-green-100 overflow-hidden">
                        <div className="flex items-center gap-2 text-green-700 font-bold mb-1">
                          <CheckCircle2 className="w-5 h-5" />
                          <span>{t('billing.couponApplied', 'Promo Code Applied Successfully!')}</span>
                        </div>
                        {validatedCouponData.organization_name && (
                          <div className="flex items-center gap-2 mt-2 text-sm text-green-800 bg-green-100/50 px-3 py-1.5 rounded-lg inline-flex">
                            <User className="w-4 h-4" />
                            <span>Enterprise access provided by <strong>{validatedCouponData.organization_name}</strong></span>
                          </div>
                        )}
                        {validatedCouponData.discount_percent > 0 && (
                          <div className="text-sm text-green-600 font-medium mt-1">
                            {validatedCouponData.discount_percent}% off your subscription
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Footer Buttons */}
                <div className="pt-6 flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isProcessing}
                    className="flex-1 px-6 py-4 rounded-xl border-2 border-slate-200 text-slate-700 font-bold hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="flex-[2] flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t('common.processing', 'Processing...')}
                      </>
                    ) : (
                      <>
                        {t('billing.continueToPayment', 'Continue to Payment')}
                        <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}